import React from "react";
import { Link } from "react-router-dom";
import SectionLabel from "@/components/SectionLabel";
import PhotoPlaceholder from "@/components/PhotoPlaceholder";
import Crown from "@/components/Crown";
import ASSETS from "@/lib/assets";

const COACH_MARQUEE = [
  "PERSONAL DEVELOPMENT", "LEADERSHIP", "CAREER COACHING",
  "BUSINESS STRATEGY", "BRAND & MARKETING", "PRIVATE 1:1",
];

const CoachMarquee = () => {
  const items = [...COACH_MARQUEE, ...COACH_MARQUEE, ...COACH_MARQUEE];
  return (
    <div className="marquee" data-testid="coaching-marquee">
      <div className="marquee-track">
        {items.map((p, i) => (
          <React.Fragment key={i}>
            <span>{p}</span>
            <span className="sep">✦</span>
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

const CATEGORIES = [
  {
    id: "personal-dev",
    eyebrow: "PERSONAL DEVELOPMENT",
    title: "Personal Development Coaching",
    desc: "For the woman who has lost herself in the hustle. This is about reconnecting with your identity, values, and vision — and building a life that actually feels like yours.",
    dark: true,
    tiers: [
      {
        tier: "Starter", name: "Foundation", duration: "4 Sessions / 1 Month",
        points: ["4 × 45-min coaching sessions", "Identity assessment & values audit", "Custom goal framework", "Email support between sessions"],
      },
      {
        tier: "Core", name: "Reconnect", duration: "8 Sessions / 2 Months", featured: true,
        points: ["8 × 45-min coaching sessions", "Full identity & values deep dive", "Mindset reprogramming toolkit", "Habit & energy design"],
      },
      {
        tier: "Immersive", name: "Full Return", duration: "12 Sessions / 3 Months",
        points: ["12 × 45-min coaching sessions", "Complete GRIT™ personal system", "Weekly Voxer access", "90-day integration roadmap"],
      },
    ],
  },
  {
    id: "leadership",
    eyebrow: "LEADERSHIP DEVELOPMENT",
    title: "Leadership Development Coaching",
    desc: "For the woman who leads others but struggles to lead herself. Build confident, values-aligned leadership from the inside out.",
    dark: false,
    tiers: [
      {
        tier: "Starter", name: "Lead with Clarity", duration: "4 Sessions / 1 Month",
        points: ["4 × 45-min leadership sessions", "Leadership style assessment", "Core leadership framework", "Situational coaching exercises"],
      },
      {
        tier: "Core", name: "Lead with Power", duration: "8 Sessions / 2 Months", featured: true,
        points: ["8 × 45-min sessions", "Executive presence development", "Difficult conversations playbook", "Leadership brand building"],
      },
      {
        tier: "Immersive", name: "Lead Your Legacy", duration: "12 Sessions / 3 Months",
        points: ["12 × 45-min sessions", "Full leadership transformation", "360 leadership audit", "Stakeholder communication strategy"],
      },
    ],
  },
  {
    id: "career",
    eyebrow: "CAREER COACHING",
    title: "Career Coaching",
    desc: "For the woman who has outgrown her role, wants to pivot, or is ready to make a move she’s been putting off.",
    dark: true,
    tiers: [
      {
        tier: "Starter", name: "Career Clarity", duration: "4 Sessions / 1 Month",
        points: ["4 × 45-min career sessions", "Career values & strengths audit", "Job search & positioning strategy", "Resume & LinkedIn review"],
      },
      {
        tier: "Core", name: "Career Momentum", duration: "8 Sessions / 2 Months", featured: true,
        points: ["8 × 45-min sessions", "Full career positioning system", "Salary negotiation coaching", "Interview prep & practice"],
      },
      {
        tier: "Immersive", name: "Career Transformation", duration: "12 Sessions / 3 Months",
        points: ["12 × 45-min sessions", "Complete career strategy roadmap", "Executive-level positioning", "Ongoing support through transition"],
      },
    ],
  },
  {
    id: "business",
    eyebrow: "BUSINESS COACHING",
    title: "Business Coaching",
    desc: "For the woman building her own thing — or ready to. Strategic business coaching with identity at its core.",
    dark: false,
    tiers: [
      {
        tier: "Starter", name: "Business Foundations", duration: "4 Sessions / 1 Month",
        points: ["4 × 45-min business sessions", "Business model clarity audit", "Offer & revenue strategy", "Core messaging framework"],
      },
      {
        tier: "Core", name: "Business Growth", duration: "8 Sessions / 2 Months", featured: true,
        points: ["8 × 45-min sessions", "Full business strategy system", "Client acquisition framework", "Pricing & packaging strategy"],
      },
      {
        tier: "Immersive", name: "Business Mastery", duration: "12 Sessions / 3 Months",
        points: ["12 × 45-min sessions", "Complete business transformation", "Revenue scaling strategy", "Monthly business review & optimization"],
      },
    ],
  },
  {
    id: "brand",
    eyebrow: "BRAND & MARKETING",
    title: "Brand & Marketing Coaching",
    desc: "For the woman ready to build a brand that actually looks and sounds like her. Rooted in identity, because the best brands come from the inside out.",
    dark: true,
    tiers: [
      {
        tier: "Starter", name: "Brand Clarity", duration: "4 Sessions / 1 Month",
        points: ["4 × 45-min brand sessions", "Brand identity & positioning audit", "Core messaging & voice development", "Visual direction framework"],
      },
      {
        tier: "Core", name: "Brand Strategy", duration: "8 Sessions / 2 Months", featured: true,
        points: ["8 × 45-min sessions", "Full brand strategy system", "Content pillars & calendar", "Platform & audience strategy"],
      },
      {
        tier: "Immersive", name: "Brand Authority", duration: "12 Sessions / 3 Months",
        points: ["12 × 45-min sessions", "Complete brand transformation", "Marketing funnel strategy", "Thought leadership roadmap"],
      },
    ],
  },
];

const Coaching = () => (
  <div data-testid="page-coaching">
    {/* HERO */}
    <section className="hero">
      <div className="hero-grid">
        <div className="hero-content">
          <SectionLabel gold>COACHING</SectionLabel>
          <h1>
            Private coaching, <em>personalized for you.</em>
          </h1>
          <p className="hero-tagline">
            Every package is tailored around your specific goals, season, and
            vision — no templates, no one-size-fits-all.
          </p>
          <div className="hero-cta-row">
            <Link to="/contact" className="btn btn-primary" data-testid="coaching-apply">
              Apply Now →
            </Link>
            <a
              href="https://calendly.com/nijahalmond/discovery-call"
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-ghost"
              data-testid="coaching-book-call"
            >
              Book Your Call
            </a>
            <a href="#personal-dev" className="btn btn-ghost" data-testid="coaching-explore">
              Explore the Tracks
            </a>
          </div>
        </div>
        <div className="hero-photo">
          <PhotoPlaceholder src={ASSETS.photoCoaching} position="center 35%" label="Nijah Almond — private 1:1 coaching session" />
        </div>
      </div>
    </section>

    <CoachMarquee />

    {/* WHY 1:1 */}
    <section className="section-pad bg-cream" data-testid="why-1-1">
      <div className="container-x">
        <div className="split tight">
          <div>
            <SectionLabel>WHY 1:1</SectionLabel>
            <div className="why-mark" aria-hidden="true">1:1</div>
          </div>
          <div>
            <h2 className="section-title text-teal">
              Fully personalized. <span className="italic-c text-coral">Entirely yours.</span>
            </h2>
            <p className="body-base">
              Group programs create community and momentum. But sometimes you
              need something more intimate — a coach who sees you, knows your
              story, and works alongside you every step.
            </p>
            <p className="body-base">
              Each package includes customized sessions, direct access, and a
              plan built entirely around you across{" "}
              <strong className="text-teal">five areas of transformation.</strong>
            </p>
          </div>
        </div>
      </div>
    </section>

    {/* BOOKING BAND — complimentary visionary call CTA */}
    <section className="booking-band" data-testid="booking-band">
      <div className="container-x">
        <div className="booking-grid">
          <div>
            <div className="eyebrow gold">COMPLIMENTARY VISIONARY CALL</div>
            <h2 className="h-display" style={{
              fontSize: "clamp(32px, 3.8vw, 48px)",
              lineHeight: 1.1,
              color: "#fff",
              marginTop: 10,
            }}>
              15 minutes. <span className="italic-c text-gold">Zero pressure.</span>
            </h2>
            <p className="body-base" style={{
              color: "rgba(255,255,255,.82)",
              marginTop: 12,
              maxWidth: 480,
            }}>
              Tell me where you are and where you want to go. I’ll tell you
              the package — or the program — that actually fits. No pitch
              deck. No hard sell.
            </p>
          </div>
          <div className="booking-cta-col">
            <a
              href="https://calendly.com/nijahalmond/discovery-call"
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-primary booking-cta-lg"
              data-testid="booking-band-cta"
            >
              Book Your Free Call →
            </a>
            <span className="booking-note">
              Free · 15 min · virtual · visionary call
            </span>
          </div>
        </div>
      </div>
    </section>

    {/* CATEGORIES */}
    {CATEGORIES.map((c) => (
      <section
        key={c.id}
        id={c.id}
        className={`cat-section ${c.dark ? "dark" : "light"} scroll-to-top-anchor`}
        data-testid={`cat-${c.id}`}
      >
        <div className="container-x">
          <div style={{ display: "flex", alignItems: "center", gap: 18, marginBottom: 18 }}>
            <Crown size={32} opacity={0.85} />
            <SectionLabel gold={c.dark}>{c.eyebrow}</SectionLabel>
          </div>

          <h2 className="cat-title" style={{ marginBottom: 14 }}>
            {c.title.split(" Coaching")[0]} <span className="italic-c">Coaching</span>
          </h2>
          <p className="cat-desc" style={{ marginBottom: 36 }}>{c.desc}</p>

          <div className="tier-grid">
            {c.tiers.map((t) => (
              <div
                key={`${c.id}-${t.tier}`}
                className={`tier-card ${c.dark ? "dark" : ""} ${t.featured ? "featured" : ""}`}
                data-testid={`tier-${c.id}-${t.tier.toLowerCase()}`}
              >
                <div className="tier-label-row">
                  <span className="tier-pill">{t.tier}</span>
                  {t.featured && <span className="tier-most">Most chosen</span>}
                </div>
                <div className="tname">{t.name}</div>
                <div className="duration">{t.duration}</div>
                <ul>
                  {t.points.map((pt, i) => <li key={i}>{pt}</li>)}
                </ul>
                <Link
                  to="/contact"
                  className={`btn ${c.dark ? "btn-primary" : "btn-teal"}`}
                  style={{ marginTop: "auto" }}
                  data-testid={`apply-${c.id}-${t.tier.toLowerCase()}`}
                >
                  Apply Now →
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>
    ))}

    {/* HOW IT WORKS */}
    <section className="section-pad bg-cream" data-testid="how-it-works">
      <div className="container-x">
        <SectionLabel>HOW IT WORKS</SectionLabel>
        <h2 className="section-title text-teal">
          Your journey, <span className="italic-c text-coral">step by step.</span>
        </h2>
        <div className="three-col" style={{ marginTop: 28 }}>
          <div className="step">
            <div className="step-num">01</div>
            <h4>Apply Now</h4>
            <p className="body-base">
              Submit your application and our team will review your goals,
              season, and vision.
            </p>
          </div>
          <div className="step">
            <div className="step-num">02</div>
            <h4>Custom Plan Built</h4>
            <p className="body-base">
              Together we design your personalized coaching plan — sessions,
              tools, timelines — built around you.
            </p>
          </div>
          <div className="step">
            <div className="step-num">03</div>
            <h4>You Rise</h4>
            <p className="body-base">
              Each session builds on the last until you’re showing up as the
              full version of yourself.
            </p>
          </div>
        </div>
      </div>
    </section>

    {/* FINAL CTA — custom (3 actions: Book Call, Apply, View Programs) */}
    <section className="cta-band" data-testid="coaching-final-cta">
      <div className="cta-inner">
        <div style={{ display: "flex", justifyContent: "center" }}>
          <Crown size={42} opacity={0.95} />
        </div>
        <div className="eyebrow gold" style={{ justifyContent: "center", marginTop: 16 }}>
          READY TO WORK TOGETHER?
        </div>
        <h2 className="h-display">
          Book your complimentary <span className="italic-c text-gold">visionary call.</span>
        </h2>
        <p className="cta-sub">
          Let’s find the perfect package for this season of your life.
        </p>
        <div className="cta-buttons">
          <a
            href="https://calendly.com/nijahalmond/discovery-call"
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-primary"
            data-testid="coaching-final-book"
          >
            Book Your Call →
          </a>
          <Link to="/contact" className="btn btn-ghost" data-testid="coaching-final-apply">
            Apply Now
          </Link>
          <Link to="/programs" className="btn btn-ghost" data-testid="coaching-final-programs">
            View Group Programs
          </Link>
        </div>
      </div>
    </section>
  </div>
);

export default Coaching;
